import { useState, useEffect, useMemo } from "react"
import { lumi } from "../lib/lumi"
import { useAuth } from "../hooks/useAuth"
import { isHoliday, toDateKey } from "../lib/koreanHolidays"

interface Activity {
  activityCode: string
  hours: number
}

interface ProjectEntry {
  projectId: string
  projectName: string
  projectCategory: string
  isVacation: boolean
  vacationType?: string
  activities: Activity[]
}

interface DayEntry {
  date: string
  dayOfWeek: string
  isHoliday: boolean
  projects: ProjectEntry[]
}

const ACTIVITY_CODES = [
  { code: "개발/설계", desc: "제품 설계, 시스템 개발, 소프트웨어 코딩" },
  { code: "영업/견적/PM/기획", desc: "고객 미팅, 견적 작성, 프로젝트 관리" },
  { code: "제작/조립/구매", desc: "부품 조달, 제품 제작, 조립 작업" },
  { code: "시험/검증/테스트", desc: "제품 테스트, 품질 검증, 성능 측정" },
  { code: "설치/납품/AS", desc: "현장 설치, 제품 납품, 유지보수" },
  { code: "지원/문서/교육", desc: "기술 지원, 문서 작성, 교육 진행" },
  { code: "미팅", desc: "회의 참석, 미팅 진행, 업무 협의" },
  { code: "이동", desc: "출장, 현장 이동, 업무 관련 이동" },
  { code: "야근", desc: "정규 근무시간 외 초과 근무" }
]

const VACATION_TYPES = [
  { id: "vacation-full", name: "연차", hours: 8 },
  { id: "vacation-morning", name: "오전반차", hours: 4 },
  { id: "vacation-afternoon", name: "오후반차", hours: 4 },
  { id: "vacation-quarter", name: "반반차", hours: 2 }
]

function getDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

function getMonday(date: Date): Date {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  d.setDate(diff)
  d.setHours(0, 0, 0, 0)
  return d
}

export function UserTimeEntry2() {
  const { user, teamId, teamName, userName } = useAuth()
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getMonday(new Date()))
  const [projects, setProjects] = useState<any[]>([])
  const [dayEntries, setDayEntries] = useState<DayEntry[]>([])
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [projectSearch, setProjectSearch] = useState("")
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null)
  const [showActivityHelp, setShowActivityHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [validationMessage, setValidationMessage] = useState("")
  const [isDraft, setIsDraft] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // 월~일 7일 생성
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(currentWeekStart)
    date.setDate(date.getDate() + i)
    return date
  })

  // 프로젝트 검색 필터링 (프로젝트명 / 분류 / 설명 부분일치, 공백 구분 AND 검색)
  const filteredProjects = useMemo(() => {
    const keyword = projectSearch.trim().toLowerCase()
    if (!keyword) return projects

    const tokens = keyword.split(/\s+/).filter(Boolean)
    return projects.filter((proj) => {
      const haystack = [
        proj?.projectName ?? "",
        proj?.category ?? "",
        proj?.description ?? ""
      ]
        .join(" ")
        .toLowerCase()
      return tokens.every((token) => haystack.includes(token))
    })
  }, [projects, projectSearch])

  useEffect(() => {
    loadProjects()
    loadDraftData()
  }, [])

  useEffect(() => {
    loadDraftData()
  }, [currentWeekStart])

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showProjectModal) {
        closeProjectModal()
      }
    }
    window.addEventListener("keydown", handleEscKey)
    return () => window.removeEventListener("keydown", handleEscKey)
  }, [showProjectModal])

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showValidationModal) {
        setShowValidationModal(false)
      }
    }
    window.addEventListener("keydown", handleEscKey)
    return () => window.removeEventListener("keydown", handleEscKey)
  }, [showValidationModal])

  async function loadProjects() {
    try {
      const result = await lumi.entities.projects.list({
        filter: { status: "active" },
        sort: { createdAt: -1 },
        limit: 500
      })
      setProjects(result.list || [])
    } catch (error) {
      console.error("프로젝트 로드 실패:", error)
    }
  }

  async function loadDraftData() {
    try {
      // 먼저 제출된 데이터 확인
      const submittedResult = await lumi.entities.time_entries2.list({
        filter: {
          userId: lumi.auth.user?.userId,
          weekStart: currentWeekStart.toISOString(),
          status: "submitted"
        },
        limit: 500
      })
      
      if (submittedResult.list && submittedResult.list.length > 0) {
        // 제출된 데이터를 화면에 표시
        const loadedDays = initializeDayEntries()
        
        submittedResult.list.forEach((entry: any) => {
          entry.dailyEntries.forEach((daily: any, dayIndex: number) => {
            if (daily.activities && daily.activities.length > 0) {
              const projectEntry: ProjectEntry = {
                projectId: entry.projectId,
                projectName: entry.projectName,
                projectCategory: entry.projectCategory,
                isVacation: daily.vacationType !== undefined && daily.vacationType !== "none",
                vacationType: daily.vacationType,
                activities: daily.activities.map((act: any) => ({
                  activityCode: act.activityCode,
                  hours: act.hours || 0
                }))
              }
              loadedDays[dayIndex].projects.push(projectEntry)
            }
          })
        })
        
        setDayEntries(loadedDays)
        setIsSubmitted(true)
        setIsDraft(false)
        setValidationMessage("✅ 이미 제출된 주간입니다. 제출된 내용을 확인하실 수 있습니다.")
        setShowValidationModal(true)
        return
      }
      
      // 임시저장 데이터 로드
      const result = await lumi.entities.time_entries2.list({
        filter: {
          userId: lumi.auth.user?.userId,
          weekStart: currentWeekStart.toISOString(),
          status: "draft"
        },
        limit: 500
      })
      
      if (result.list && result.list.length > 0) {
        // DB에서 로드한 데이터를 요일별 구조로 변환
        const loadedDays = initializeDayEntries()
        
        result.list.forEach((entry: any) => {
          entry.dailyEntries.forEach((daily: any, dayIndex: number) => {
            if (daily.activities && daily.activities.length > 0) {
              // 해당 날짜에 프로젝트 추가
              const projectEntry: ProjectEntry = {
                projectId: entry.projectId,
                projectName: entry.projectName,
                projectCategory: entry.projectCategory,
                isVacation: daily.vacationType !== undefined && daily.vacationType !== "none",
                vacationType: daily.vacationType,
                activities: daily.activities.map((act: any) => ({
                  activityCode: act.activityCode,
                  hours: act.hours || 0
                }))
              }
              loadedDays[dayIndex].projects.push(projectEntry)
            }
          })
        })
        
        setDayEntries(loadedDays)
        setIsSubmitted(false)
        setIsDraft(true)
        setValidationMessage("💾 임시 저장된 데이터를 불러왔습니다.")
        setShowValidationModal(true)
      } else {
        setDayEntries(initializeDayEntries())
        setIsSubmitted(false)
        setIsDraft(false)
      }
    } catch (error) {
      console.error("임시 저장 데이터 로드 실패:", error)
      setDayEntries(initializeDayEntries())
    }
  }

  function initializeDayEntries(): DayEntry[] {
    return weekDates.map((date, index) => ({
      date: date.toISOString(),
      dayOfWeek: ["일", "월", "화", "수", "목", "금", "토"][date.getDay()],
      isHoliday: isHoliday(toDateKey(date)),
      projects: []
    }))
  }

  function openProjectModal(dayIndex: number) {
    if (dayEntries[dayIndex].isHoliday) {
      setValidationMessage("⚠️ 공휴일에는 시간을 입력할 수 없습니다.")
      setShowValidationModal(true)
      return
    }
    setSelectedDayIndex(dayIndex)
    setProjectSearch("")
    setShowProjectModal(true)
  }

  function closeProjectModal() {
    setShowProjectModal(false)
    setProjectSearch("")
  }

  function addProject(project: any) {
    if (selectedDayIndex === null) return
    
    // 연차 사용 시 프로젝트 추가 불가
    const hasFullVacation = dayEntries[selectedDayIndex].projects.some(
      p => p.isVacation && p.vacationType === "연차"
    )
    if (hasFullVacation) {
      setValidationMessage("⚠️ 연차를 사용한 날에는 프로젝트를 추가할 수 없습니다.")
      setShowValidationModal(true)
      return
    }
    
    // 중복 체크
    if (dayEntries[selectedDayIndex].projects.some(p => p.projectId === project._id)) {
      setValidationMessage("⚠️ 이미 추가된 프로젝트입니다.")
      setShowValidationModal(true)
      return
    }

    const newProject: ProjectEntry = {
      projectId: project._id,
      projectName: project.projectName,
      projectCategory: project.category,
      isVacation: false,
      activities: []
    }

    const newDayEntries = [...dayEntries]
    newDayEntries[selectedDayIndex].projects.push(newProject)
    setDayEntries(newDayEntries)
    closeProjectModal()
  }

  function addVacation(vacation: typeof VACATION_TYPES[0]) {
    if (selectedDayIndex === null) return
    
    // 토일 체크 (월요일=0, 토요일=5, 일요일=6)
    if (selectedDayIndex === 5 || selectedDayIndex === 6) {
      setValidationMessage("⚠️ 토요일과 일요일에는 연차/반차를 사용할 수 없습니다.")
      setShowValidationModal(true)
      return
    }
    
    // 중복 체크
    if (dayEntries[selectedDayIndex].projects.some(p => p.projectId === vacation.id)) {
      setValidationMessage("⚠️ 이미 추가된 연차/반차입니다.")
      setShowValidationModal(true)
      return
    }
    
    // 같은 날 다른 연차/반차가 있는지 확인
    const hasVacation = dayEntries[selectedDayIndex].projects.some(p => p.isVacation)
    if (hasVacation) {
      setValidationMessage("⚠️ 하루에 하나의 연차/반차만 사용할 수 있습니다.")
      setShowValidationModal(true)
      return
    }
    
    // 연차 사용 시 이미 입력된 프로젝트가 있는지 확인
    if (vacation.name === "연차" && dayEntries[selectedDayIndex].projects.length > 0) {
      setValidationMessage("⚠️ 연차를 사용하려면 먼저 추가된 프로젝트를 삭제해야 합니다.")
      setShowValidationModal(true)
      return
    }

    const newProject: ProjectEntry = {
      projectId: vacation.id,
      projectName: vacation.name,
      projectCategory: vacation.name,
      isVacation: true,
      vacationType: vacation.name,
      activities: []
    }

    const newDayEntries = [...dayEntries]
    newDayEntries[selectedDayIndex].projects.push(newProject)
    setDayEntries(newDayEntries)
    closeProjectModal()
  }

  function removeProject(dayIndex: number, projectId: string) {
    const newDayEntries = [...dayEntries]
    newDayEntries[dayIndex].projects = newDayEntries[dayIndex].projects.filter(
      p => p.projectId !== projectId
    )
    setDayEntries(newDayEntries)
  }

  function updateActivity(dayIndex: number, projectId: string, activityCode: string, hours: number) {
    // 일일 최대 8시간 검증 (야근은 제외, 연차/반차/반반차 고려)
    if (activityCode !== "야근") {
      const currentDayTotal = getDailyTotal(dayIndex)
      const project = dayEntries[dayIndex].projects.find(p => p.projectId === projectId)
      if (!project) return
      
      // 연차 시간 계산 (VACATION_TYPES 기준)
      let vacationHours = 0
      dayEntries[dayIndex].projects.forEach(proj => {
        if (proj.isVacation) {
          const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
          if (vacType) {
            vacationHours = vacType.hours
          } else if (proj.activities.length > 0) {
            vacationHours = proj.activities[0].hours
          }
        }
      })
      
      // 연차 사용 시 입력 가능한 최대 시간 계산
      // 연차(8h 차감) = 0시간 입력 가능
      // 반차(4h 차감) = 4시간 입력 가능
      // 반반차(2h 차감) = 6시간 입력 가능
      const maxAllowedHours = 8 - vacationHours
      
      const existingActivity = project.activities.find(a => a.activityCode === activityCode)
      const currentActivityHours = existingActivity?.hours || 0
      const newDayTotal = currentDayTotal - currentActivityHours + hours
      
      if (newDayTotal > maxAllowedHours) {
        const availableHours = maxAllowedHours - (currentDayTotal - currentActivityHours)
        let message = `⚠️ 하루 최대 ${maxAllowedHours}시간을 초과했습니다.\n\n현재 입력 가능: ${availableHours.toFixed(1)}시간`
        if (vacationHours > 0) {
          const vacationType = vacationHours === 8 ? "연차" : vacationHours === 4 ? "반차" : "반반차"
          message += `\n\n${vacationType} 사용으로 ${vacationHours}시간이 차감되었습니다.`
        }
        setValidationMessage(message)
        setShowValidationModal(true)
        return
      }
    }

    // 일일 전체 활동코드 합계 24시간 검증 (정규 + 야근 포함)
    const project = dayEntries[dayIndex].projects.find(p => p.projectId === projectId)
    if (!project) return
    
    const existingActivity = project.activities.find(a => a.activityCode === activityCode)
    const currentActivityHours = existingActivity?.hours || 0
    
    let currentDayTotalAll = 0
    dayEntries[dayIndex].projects.forEach(proj => {
      proj.activities.forEach(act => {
        currentDayTotalAll += act.hours
      })
    })
    
    const newDayTotalAll = currentDayTotalAll - currentActivityHours + hours
    
    if (newDayTotalAll > 24) {
      const availableHours = 24 - (currentDayTotalAll - currentActivityHours)
      setValidationMessage(`⚠️ 하루 전체 활동시간이 최대 24시간을 초과했습니다.\n\n현재 입력 가능: ${availableHours.toFixed(1)}시간`)
      setShowValidationModal(true)
      return
    }

    const newDayEntries = [...dayEntries]
    const targetProject = newDayEntries[dayIndex].projects.find(p => p.projectId === projectId)
    if (!targetProject) return

    const targetActivity = targetProject.activities.find(a => a.activityCode === activityCode)
    if (targetActivity) {
      targetActivity.hours = hours
      if (hours === 0) {
        targetProject.activities = targetProject.activities.filter(a => a.activityCode !== activityCode)
      }
    } else if (hours > 0) {
      targetProject.activities.push({
        activityCode,
        hours
      })
    }

    setDayEntries(newDayEntries)
  }

  function getDailyTotal(dayIndex: number): number {
    let total = 0
    dayEntries[dayIndex].projects.forEach(project => {
      // 연차/반차는 제외하고 계산
      if (!project.isVacation) {
        project.activities.forEach(activity => {
          if (activity.activityCode !== "야근") {
            total += activity.hours
          }
        })
      }
    })
    return total
  }

  function getDailyOvertimeTotal(dayIndex: number): number {
    let total = 0
    dayEntries[dayIndex].projects.forEach(project => {
      project.activities.forEach(activity => {
        if (activity.activityCode === "야근") {
          total += activity.hours
        }
      })
    })
    return total
  }

  function getWeeklyTotal(): number {
    let total = 0
    dayEntries.forEach(day => {
      day.projects.forEach(project => {
        // 연차/반차/반반차는 제외하고 계산
        if (!project.isVacation) {
          project.activities.forEach(activity => {
            total += activity.hours
          })
        }
      })
    })
    return total
  }

  function getWeeklyRegularHours(): number {
    let total = 0
    dayEntries.forEach(day => {
      day.projects.forEach(project => {
        // 연차/반차는 제외하고 계산
        if (!project.isVacation) {
          project.activities.forEach(activity => {
            if (activity.activityCode !== "야근") {
              total += activity.hours
            }
          })
        }
      })
    })
    return total
  }

  function getWeeklyOvertimeHours(): number {
    let total = 0
    dayEntries.forEach(day => {
      day.projects.forEach(project => {
        project.activities.forEach(activity => {
          if (activity.activityCode === "야근") {
            total += activity.hours
          }
        })
      })
    })
    return total
  }

  async function saveDraft() {
    const hasData = dayEntries.some(day => day.projects.length > 0)
    if (!hasData) {
      setValidationMessage("저장할 데이터가 없습니다.")
      setShowValidationModal(true)
      return
    }

    // 요일별 일일합계 검증
    const validationErrors = []
    for (let dayIndex = 0; dayIndex < dayEntries.length; dayIndex++) {
      const day = dayEntries[dayIndex]
      if (day.isHoliday) continue
      
      const dailyTotal = getDailyTotal(dayIndex)
      let vacationHours = 0
      day.projects.forEach(proj => {
        if (proj.isVacation) {
          const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
          if (vacType) {
            vacationHours = vacType.hours
          } else if (proj.activities.length > 0) {
            vacationHours = proj.activities[0].hours
          }
        }
      })
      
      const maxAllowedHours = 8 - vacationHours
      if (dailyTotal > maxAllowedHours) {
        const dayName = ["월", "화", "수", "목", "금", "토", "일"][dayIndex]
        validationErrors.push(`${dayName}요일: ${dailyTotal}시간 입력됨 (최대 ${maxAllowedHours}시간)`)
      }
    }
    
    if (validationErrors.length > 0) {
      setValidationMessage(`⚠️ 다음 요일의 투입시간이 제한을 초과했습니다:\n\n${validationErrors.join('\n')}\n\n각 요일의 투입시간을 수정해주세요.`)
      setShowValidationModal(true)
      return
    }

    setLoading(true)
    try {
      let currentTeamId = teamId
      let currentTeamName = teamName
      let currentUserName = userName
      
      if (!currentTeamId || !currentTeamName || !currentUserName) {
        const profileResult = await lumi.entities.userProfiles3.list({
          filter: { userId: lumi.auth.user?.userId },
          limit: 1
        })
        
        if (profileResult.list && profileResult.list.length > 0) {
          currentTeamId = profileResult.list[0].teamId || ""
          currentTeamName = profileResult.list[0].teamName || ""
          currentUserName = profileResult.list[0].userName || lumi.auth.user?.userName || ""
        }
      }

      // 기존 임시저장 삭제 (deleteMany 사용)
      const existingDrafts = await lumi.entities.time_entries2.list({
        filter: {
          userId: lumi.auth.user?.userId,
          weekStart: currentWeekStart.toISOString(),
          status: "draft"
        },
        limit: 500
      })

      if (existingDrafts.list && existingDrafts.list.length > 0) {
        const deleteIds = existingDrafts.list.map(draft => draft._id)
        await lumi.entities.time_entries2.deleteMany(deleteIds)
      }

      // 프로젝트별로 저장
      const projectMap = new Map<string, any>()
      
      dayEntries.forEach((day, dayIndex) => {
        day.projects.forEach(project => {
          if (!projectMap.has(project.projectId)) {
            projectMap.set(project.projectId, {
              projectId: project.projectId,
              projectName: project.projectName,
              projectCategory: project.projectCategory,
              dailyEntries: weekDates.map((date, i) => ({
                date: date.toISOString(),
                dayOfWeek: ["일", "월", "화", "수", "목", "금", "토"][date.getDay()],
                vacationType: i === dayIndex && project.isVacation ? project.vacationType : "none",
                activities: []
              }))
            })
          }
          
          const projectData = projectMap.get(project.projectId)
          if (project.isVacation) {
            // 연차/반차의 경우 vacationType 설정하고 시간 정보를 activities에 추가
            projectData.dailyEntries[dayIndex].vacationType = project.vacationType || "none"
            const vacationHours = VACATION_TYPES.find(v => v.name === project.vacationType)?.hours || 0
            projectData.dailyEntries[dayIndex].activities = [{ activityCode: project.vacationType || "", hours: vacationHours }]
          } else {
            projectData.dailyEntries[dayIndex].activities = [...project.activities]
          }
        })
      })

      // createMany를 사용한 배치 생성
      const entriesToCreate = Array.from(projectMap.values()).map(projectData => {
        const totalHours = projectData.dailyEntries.reduce((sum: number, day: any) => {
          return sum + day.activities.reduce((s: number, a: any) => s + a.hours, 0)
        }, 0)

        return {
          userId: lumi.auth.user?.userId,
          userName: currentUserName,
          teamId: currentTeamId,
          teamName: currentTeamName,
          projectId: projectData.projectId,
          projectName: projectData.projectName,
          projectCategory: projectData.projectCategory,
          weekStart: currentWeekStart.toISOString(),
          weekEnd: weekDates[6].toISOString(),
          dailyEntries: projectData.dailyEntries,
          totalHours,
          status: "draft",
          isTemporary: true,
          creator: lumi.auth.user?.userId || "",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      })

      if (entriesToCreate.length > 0) {
        await lumi.entities.time_entries2.createMany(entriesToCreate)
      }

      setValidationMessage("💾 임시 저장되었습니다! 나중에 수정할 수 있습니다.")
      setShowValidationModal(true)
      setIsDraft(true)
    } catch (error) {
      console.error("임시 저장 실패:", error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setValidationMessage(`임시 저장에 실패했습니다.\n\n에러: ${errorMessage}`)
      setShowValidationModal(true)
    } finally {
      setLoading(false)
    }
  }

  async function saveTimeEntries() {
    const hasData = dayEntries.some(day => day.projects.length > 0)
    if (!hasData) {
      setValidationMessage("저장할 데이터가 없습니다.")
      setShowValidationModal(true)
      return
    }

    // 요일별 일일합계 검증
    const validationErrors = []
    for (let dayIndex = 0; dayIndex < dayEntries.length; dayIndex++) {
      const day = dayEntries[dayIndex]
      if (day.isHoliday) continue
      
      const dailyTotal = getDailyTotal(dayIndex)
      let vacationHours = 0
      day.projects.forEach(proj => {
        if (proj.isVacation) {
          const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
          if (vacType) {
            vacationHours = vacType.hours
          } else if (proj.activities.length > 0) {
            vacationHours = proj.activities[0].hours
          }
        }
      })
      
      const maxAllowedHours = 8 - vacationHours
      if (dailyTotal > maxAllowedHours) {
        const dayName = ["월", "화", "수", "목", "금", "토", "일"][dayIndex]
        validationErrors.push(`${dayName}요일: ${dailyTotal}시간 입력됨 (최대 ${maxAllowedHours}시간)`)
      }
    }
    
    if (validationErrors.length > 0) {
      setValidationMessage(`⚠️ 다음 요일의 투입시간이 제한을 초과했습니다:\n\n${validationErrors.join('\n')}\n\n각 요일의 투입시간을 수정해주세요.`)
      setShowValidationModal(true)
      return
    }

    // 주간 최대 40시간 검증 (정규 근무시간만)
    const weeklyRegular = getWeeklyRegularHours()
    if (weeklyRegular > 40) {
      setValidationMessage(`⚠️ 주간 정규 근무시간이 최대 40시간을 초과했습니다.\n\n현재: ${weeklyRegular}시간\n최대: 40시간`)
      setShowValidationModal(true)
      return
    }

    setLoading(true)
    try {
      let currentTeamId = teamId
      let currentTeamName = teamName
      let currentUserName = userName
      
      if (!currentTeamId || !currentTeamName || !currentUserName) {
        const profileResult = await lumi.entities.userProfiles3.list({
          filter: { userId: lumi.auth.user?.userId },
          limit: 1
        })
        
        if (profileResult.list && profileResult.list.length > 0) {
          currentTeamId = profileResult.list[0].teamId || ""
          currentTeamName = profileResult.list[0].teamName || ""
          currentUserName = profileResult.list[0].userName || lumi.auth.user?.userName || ""
        }
      }

      // 기존 데이터 삭제 (deleteMany 사용)
      const existingEntries = await lumi.entities.time_entries2.list({
        filter: {
          userId: lumi.auth.user?.userId,
          weekStart: currentWeekStart.toISOString()
        },
        limit: 500
      })

      if (existingEntries.list && existingEntries.list.length > 0) {
        const deleteIds = existingEntries.list.map(entry => entry._id)
        await lumi.entities.time_entries2.deleteMany(deleteIds)
      }

      // 프로젝트별로 저장
      const projectMap = new Map<string, any>()
      
      dayEntries.forEach((day, dayIndex) => {
        day.projects.forEach(project => {
          if (!projectMap.has(project.projectId)) {
            projectMap.set(project.projectId, {
              projectId: project.projectId,
              projectName: project.projectName,
              projectCategory: project.projectCategory,
              dailyEntries: weekDates.map((date, i) => ({
                date: date.toISOString(),
                dayOfWeek: ["일", "월", "화", "수", "목", "금", "토"][date.getDay()],
                vacationType: i === dayIndex && project.isVacation ? project.vacationType : "none",
                activities: []
              }))
            })
          }
          
          const projectData = projectMap.get(project.projectId)
          if (project.isVacation) {
            // 연차/반차의 경우 vacationType 설정하고 시간 정보를 activities에 추가
            projectData.dailyEntries[dayIndex].vacationType = project.vacationType || "none"
            const vacationHours = VACATION_TYPES.find(v => v.name === project.vacationType)?.hours || 0
            projectData.dailyEntries[dayIndex].activities = [{ activityCode: project.vacationType || "", hours: vacationHours }]
          } else {
            projectData.dailyEntries[dayIndex].activities = [...project.activities]
          }
        })
      })

      // createMany를 사용한 배치 생성
      const entriesToCreate = Array.from(projectMap.values()).map(projectData => {
        const totalHours = projectData.dailyEntries.reduce((sum: number, day: any) => {
          return sum + day.activities.reduce((s: number, a: any) => s + a.hours, 0)
        }, 0)

        return {
          userId: lumi.auth.user?.userId,
          userName: currentUserName,
          teamId: currentTeamId,
          teamName: currentTeamName,
          projectId: projectData.projectId,
          projectName: projectData.projectName,
          projectCategory: projectData.projectCategory,
          weekStart: currentWeekStart.toISOString(),
          weekEnd: weekDates[6].toISOString(),
          dailyEntries: projectData.dailyEntries,
          totalHours,
          status: "submitted",
          isTemporary: false,
          creator: lumi.auth.user?.userId || "",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      })

      if (entriesToCreate.length > 0) {
        await lumi.entities.time_entries2.createMany(entriesToCreate)
      }

      setValidationMessage("✅ 최종 제출되었습니다!")
      setShowValidationModal(true)
      setIsSubmitted(true)
      setDayEntries(initializeDayEntries())
    } catch (error) {
      console.error("최종 제출 실패:", error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      setValidationMessage(`최종 제출에 실패했습니다.\n\n에러: ${errorMessage}`)
      setShowValidationModal(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">주간 시간 입력 (요일별)</h2>
            <p className="text-sm sm:text-base text-gray-600">요일별로 프로젝트를 추가하고 활동 시간을 입력하세요</p>
          </div>
          <button
            onClick={() => setShowActivityHelp(!showActivityHelp)}
            className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden sm:inline">활동 코드 도움말</span>
            <span className="sm:hidden">도움말</span>
          </button>
        </div>
      </div>

      {showActivityHelp && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-orange-900 mb-3">활동 코드 설명</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ACTIVITY_CODES.map(activity => (
              <div key={activity.code} className="bg-white rounded-lg p-3">
                <p className="font-medium text-gray-900">{activity.code}</p>
                <p className="text-sm text-gray-600">{activity.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 주간 네비게이션 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              const newDate = new Date(currentWeekStart)
              newDate.setDate(newDate.getDate() - 7)
              setCurrentWeekStart(newDate)
            }}
            className="px-2 sm:px-4 py-2 text-xs sm:text-base text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ← <span className="hidden sm:inline">이전 주</span>
          </button>
          <div className="flex-1 text-center px-2">
            <p className="text-sm sm:text-lg font-semibold text-gray-900">
              {currentWeekStart.getFullYear()}년 {currentWeekStart.getMonth() + 1}월 {Math.ceil(currentWeekStart.getDate() / 7)}주차
            </p>
            <p className="text-xs sm:text-sm text-gray-600">
              {currentWeekStart.getMonth() + 1}/{currentWeekStart.getDate()} - {weekDates[6].getMonth() + 1}/{weekDates[6].getDate()}
            </p>
          </div>
          <button
            onClick={() => {
              const today = new Date()
              const nextWeekStart = new Date(currentWeekStart)
              nextWeekStart.setDate(nextWeekStart.getDate() + 7)
              
              if (today < nextWeekStart) {
                setValidationMessage("⚠️ 아직 다음 주가 시작되지 않았습니다. 해당 주차가 되면 입력할 수 있습니다.")
                setShowValidationModal(true)
                return
              }
              
              setCurrentWeekStart(nextWeekStart)
            }}
            className="px-2 sm:px-4 py-2 text-xs sm:text-base text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="hidden sm:inline">다음 주</span> →
          </button>
        </div>
      </div>

      {/* 상단 저장 버튼 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">이번 주 총 입력시간</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">
              {getWeeklyTotal()} 시간
            </p>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-xs text-gray-500">정규 근무</p>
                <p className="text-lg font-semibold text-blue-700">{getWeeklyRegularHours()}h</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">야근</p>
                <p className="text-lg font-semibold text-orange-600">{getWeeklyOvertimeHours()}h</p>
              </div>
            </div>
            {getWeeklyRegularHours() > 40 && (
              <p className="text-sm text-red-600 mt-1">⚠️ 주간 정규 근무시간이 최대 40시간을 초과했습니다</p>
            )}
            {isDraft && (
              <p className="text-sm text-orange-600 mt-1">💾 임시 저장됨</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={saveDraft}
              disabled={loading || isSubmitted}
              className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "저장 중..." : "임시저장"}
            </button>
            <button
              onClick={saveTimeEntries}
              disabled={loading || isSubmitted}
              className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "제출 중..." : "최종 제출"}
            </button>
          </div>
        </div>
      </div>

      {isSubmitted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-green-800 font-medium">✅ 이미 제출된 주간입니다.</p>
          <p className="text-xs text-green-700 mt-1">아래에서 제출된 내용을 확인하실 수 있습니다. 수정이 필요한 경우 관리자에게 문의하세요.</p>
        </div>
      )}

      {/* 요일별 카드 */}
      <div className="space-y-4 mb-6">
        {dayEntries.map((day, dayIndex) => {
          const dailyTotal = getDailyTotal(dayIndex)
          const isWeekend = dayIndex === 5 || dayIndex === 6
          
          return (
            <div 
              key={dayIndex} 
              className={`bg-white rounded-xl shadow-sm border-2 p-4 sm:p-6 ${
                day.isHoliday 
                  ? "border-red-200 bg-red-50" 
                  : isWeekend 
                  ? "border-orange-200" 
                  : "border-gray-200"
              }`}
            >
              {/* 요일 헤더 */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                    {day.dayOfWeek}요일 ({weekDates[dayIndex].getMonth() + 1}/{weekDates[dayIndex].getDate()})
                  </h3>
                  {day.isHoliday && (
                    <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                      공휴일
                    </span>
                  )}
                  {isWeekend && !day.isHoliday && (
                    <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                      주말
                    </span>
                  )}
                </div>
                <div className="text-right">
                  {isWeekend ? (
                    // 토/일: 야근 합계만 표시
                    <>
                      <p className="text-sm text-gray-600">야근 합계</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {getDailyOvertimeTotal(dayIndex)}h
                      </p>
                    </>
                  ) : (
                    // 평일: 일일합계와 야근 합계 둘 다 표시
                    <>
                      <p className="text-sm text-gray-600">일일 합계</p>
                      <p className={`text-2xl font-bold ${dailyTotal > (() => {
                        let vacationHours = 0
                        dayEntries[dayIndex].projects.forEach(proj => {
                          if (proj.isVacation) {
                            const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
                            if (vacType) vacationHours = vacType.hours
                            else if (proj.activities.length > 0) vacationHours = proj.activities[0].hours
                          }
                        })
                        return 8 - vacationHours
                      })() ? "text-red-600" : "text-gray-900"}`}>
                        {dailyTotal}h / {(() => {
                          let vacationHours = 0
                          dayEntries[dayIndex].projects.forEach(proj => {
                            if (proj.isVacation) {
                              const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
                              if (vacType) vacationHours = vacType.hours
                              else if (proj.activities.length > 0) vacationHours = proj.activities[0].hours
                            }
                          })
                          return 8 - vacationHours
                        })()}h
                      </p>
                      {dailyTotal > (() => {
                        let vacationHours = 0
                        dayEntries[dayIndex].projects.forEach(proj => {
                          if (proj.isVacation) {
                            const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
                            if (vacType) vacationHours = vacType.hours
                            else if (proj.activities.length > 0) vacationHours = proj.activities[0].hours
                          }
                        })
                        return 8 - vacationHours
                      })() && (
                        <p className="text-xs text-red-600 mt-1">⚠️ {(() => {
                          let vacationHours = 0
                          dayEntries[dayIndex].projects.forEach(proj => {
                            if (proj.isVacation) {
                              const vacType = VACATION_TYPES.find(v => v.name === proj.vacationType)
                              if (vacType) vacationHours = vacType.hours
                              else if (proj.activities.length > 0) vacationHours = proj.activities[0].hours
                            }
                          })
                          return 8 - vacationHours
                        })()}시간 초과</p>
                      )}
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">야근 합계</p>
                        <p className="text-lg font-semibold text-orange-600">{getDailyOvertimeTotal(dayIndex)}h</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* 프로젝트 추가 버튼 */}
              <div className="mb-4">
                <button
                  onClick={() => openProjectModal(dayIndex)}
                  disabled={isSubmitted || day.isHoliday}
                  className="w-full sm:w-auto px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  + 프로젝트/연차 추가
                </button>
              </div>

              {/* 프로젝트 목록 */}
              {day.projects.length > 0 ? (
                <div className="space-y-3">
                  {day.projects.map((project, projIndex) => (
                    <div key={projIndex} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{project.projectName}</h4>
                          <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                            project.projectCategory === "납품장비" ? "bg-blue-100 text-orange-700" :
                            project.projectCategory === "제품개발" ? "bg-green-100 text-green-700" :
                            project.projectCategory === "연구개발" ? "bg-purple-100 text-purple-700" :
                            project.projectCategory === "경영지원" ? "bg-orange-100 text-orange-700" :
                            project.projectCategory === "연차" ? "bg-pink-100 text-pink-700" :
                            project.projectCategory === "반차" ? "bg-pink-100 text-pink-700" :
                            project.projectCategory === "반반차" ? "bg-pink-100 text-pink-700" :
                            "bg-gray-100 text-gray-700"
                          }`}>
                            {project.projectCategory}
                          </span>
                        </div>
                        <button
                          onClick={() => removeProject(dayIndex, project.projectId)}
                          disabled={isSubmitted}
                          className="text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {project.isVacation ? (
                        // 연차/반차 표시
                        <div className="text-sm text-gray-700">
                          <p>사용 시간: <span className="font-semibold">{VACATION_TYPES.find(v => v.name === project.vacationType)?.hours || 0}시간</span></p>
                        </div>
                      ) : (
                        // 활동 코드 입력
                        <div className="flex gap-1 overflow-x-auto pb-2">
                          {ACTIVITY_CODES
                            .filter(activity => {
                              // 주말(토요일=5, 일요일=6)에는 야근만 표시
                              if (isWeekend) {
                                return activity.code === "야근"
                              }
                              return true
                            })
                            .map(activity => {
                            const existingActivity = project.activities.find(a => a.activityCode === activity.code)
                            return (
                              <div key={activity.code} className="flex flex-col min-w-[90px]">
                                <label className="text-[10px] text-gray-600 mb-1 truncate" title={activity.code}>
                                  {activity.code}
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  max="8"
                                  step="0.5"
                                  value={existingActivity?.hours || ""}
                                  onChange={(e) => updateActivity(dayIndex, project.projectId, activity.code, parseFloat(e.target.value) || 0)}
                                  disabled={isSubmitted}
                                  placeholder="0"
                                  className="w-full text-center border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100"
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {day.isHoliday ? "공휴일입니다" : "프로젝트를 추가하세요"}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 주간 합계 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">이번 주 총 입력시간</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">
              {getWeeklyTotal()} 시간
            </p>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-xs text-gray-500">정규 근무</p>
                <p className="text-lg font-semibold text-blue-700">{getWeeklyRegularHours()}h</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">야근</p>
                <p className="text-lg font-semibold text-orange-600">{getWeeklyOvertimeHours()}h</p>
              </div>
            </div>
            {getWeeklyRegularHours() > 40 && (
              <p className="text-sm text-red-600 mt-1">⚠️ 주간 정규 근무시간이 최대 40시간을 초과했습니다</p>
            )}
            {isDraft && (
              <p className="text-sm text-orange-600 mt-1">💾 임시 저장됨</p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={saveDraft}
              disabled={loading || isSubmitted}
              className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "저장 중..." : "임시저장"}
            </button>
            <button
              onClick={saveTimeEntries}
              disabled={loading || isSubmitted}
              className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "제출 중..." : "최종 제출"}
            </button>
          </div>
        </div>
      </div>

      {/* 프로젝트 선택 모달 */}
      {showProjectModal && selectedDayIndex !== null && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeProjectModal}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">프로젝트 / 연차 선택</h3>
            
            <div className="mb-6">
              <h4 className="font-bold text-gray-700 mb-2">연차/반차</h4>
              <div className="grid grid-cols-2 gap-2">
                {VACATION_TYPES.map((vac) => (
                  <button
                    key={vac.id}
                    onClick={() => addVacation(vac)}
                    className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200"
                  >
                    {vac.name} ({vac.hours}h)
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-gray-700">프로젝트</h4>
                <span className="text-xs text-gray-500">
                  {projectSearch.trim()
                    ? `검색 결과 ${filteredProjects.length}건`
                    : `전체 ${projects.length}건`}
                </span>
              </div>

              <div className="relative mb-3">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  autoFocus
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="프로젝트명 또는 분류로 검색"
                  className="w-full border border-gray-300 rounded-lg py-2 pl-9 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                {projectSearch ? (
                  <button
                    type="button"
                    onClick={() => setProjectSearch("")}
                    aria-label="검색어 초기화"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : null}
              </div>

              {filteredProjects.length > 0 ? (
                <div className="space-y-2">
                  {filteredProjects.map((proj) => {
                    const isAlreadyAdded = dayEntries[selectedDayIndex].projects.some(p => p.projectId === proj._id)
                    return (
                      <button
                        key={proj._id}
                        onClick={() => addProject(proj)}
                        disabled={isAlreadyAdded}
                        className={`w-full px-4 py-3 text-left rounded-lg transition-colors ${
                          isAlreadyAdded 
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed opacity-60" 
                            : "bg-gray-100 hover:bg-gray-200"
                        }`}
                      >
                        <p className="font-medium">{proj.projectName} {isAlreadyAdded && "(추가됨)"}</p>
                        <p className="text-sm text-gray-600">{proj.category}</p>
                        {proj.description && (
                          <p className="text-xs text-gray-500 mt-1">{proj.description}</p>
                        )}
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-10 px-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                  {projectSearch.trim() ? (
                    <>
                      <p className="text-sm text-gray-700">
                        &apos;{projectSearch.trim()}&apos;와 일치하는 프로젝트가 없습니다.
                      </p>
                      <button
                        type="button"
                        onClick={() => setProjectSearch("")}
                        className="mt-3 px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        검색 초기화
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">등록된 활성 프로젝트가 없습니다.</p>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={closeProjectModal}
              className="mt-4 w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {/* 검증 모달 */}
      {showValidationModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowValidationModal(false)}
        >
          <div 
            className="bg-white rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-gray-800 whitespace-pre-line mb-4">{validationMessage}</p>
            <button
              onClick={() => setShowValidationModal(false)}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
